<template>
  <div class="directorio-page">
    <!-- 1. Header Hero -->
    <header class="directorio-hero bg-primary-dark-base text-white py-5 px-3">
      <div class="container text-center py-4">
        <span
          class="badge badge-success-base text-uppercase px-3 py-2
            font-weight-bold mb-3 rounded-pill"
        >
          Los mejores profesionales están en el...
        </span>
        <h1 class="display-4 font-weight-bold mb-3">
          Directorio Médico AMII
        </h1>
        <p class="lead max-w-700 mx-auto opacity-90 mb-4">
          Encuentra y agenda con especialistas e instituciones calificadas.
          Atención personalizada, historias clínicas seguras y reservas
          directas.
        </p>
        <div class="d-flex justify-content-center flex-wrap gap-2">
          <a
            href="#buscador-general"
            class="btn btn-success-base btn-md px-4 py-2 font-weight-bold
              rounded-pill text-white text-decoration-none"
            @click.prevent="scrollToSearch"
          >
            <font-awesome-icon
              icon="search"
              class="mr-2"
            />
            Buscar Especialista
          </a>
          <a
            v-if="featuredCategories && featuredCategories.length"
            href="#medicos-destacados"
            class="btn btn-outline-light btn-md px-4 py-2 font-weight-bold
              rounded-pill text-decoration-none"
            @click.prevent="scrollToFeatured"
          >
            <font-awesome-icon
              icon="star"
              class="mr-2 text-warning"
            />
            Ver Destacados
          </a>
          <a
            href="https://paciente.amii.app"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-outline-light btn-md px-4 py-2 font-weight-bold
              rounded-pill text-decoration-none"
          >
            <font-awesome-icon
              icon="user"
              class="mr-2 text-success-base"
            />
            Acceder como paciente
          </a>
        </div>
      </div>
    </header>

    <!-- 2. Médicos Destacados -->
    <section
      v-if="featuredCategories && featuredCategories.length"
      id="medicos-destacados"
      class="py-5 bg-light border-bottom"
    >
      <div class="container">
        <div class="text-center mb-5">
          <span
            class="badge badge-primary-base px-3 py-2 text-uppercase
              font-weight-bold mb-2"
          >
            Excelencia Médica
          </span>
          <h2 class="h2 font-weight-bold text-dark-blue mb-1">
            Médicos Destacados
          </h2>
          <p class="text-muted small">
            Profesionales de la salud mejor valorados por sus pacientes
          </p>
        </div>

        <div
          v-for="cat in featuredCategories"
          :key="cat.categoryName"
          class="mb-5"
        >
          <div class="d-flex align-items-center mb-3">
            <h3 class="h4 font-weight-bold text-dark-blue mb-0">
              {{ cat.categoryName }}
            </h3>
            <span class="badge badge-light ml-2 border">
              Top Especialistas
            </span>
          </div>

          <div class="row">
            <div
              v-for="doc in cat.doctors"
              :key="doc.id"
              class="col-12 col-md-6 col-lg-4 mb-4"
            >
              <div
                class="card h-100 shadow-sm border-0 rounded-xl
                  featured-doctor-card p-4 text-center"
              >
                <!-- Avatar with elegant ambient glow -->
                <div class="featured-avatar-wrapper">
                  <div class="featured-glow-halo" />
                  <img
                    :src="getDoctorAvatar(doc)"
                    :alt="getDoctorFullName(doc)"
                    class="featured-avatar"
                  >
                </div>

                <h4 class="h5 font-weight-bold text-dark-blue mb-1">
                  {{ getDoctorFullName(doc) }}
                </h4>

                <p class="text-primary-base font-weight-bold small mb-2">
                  {{ doc.primarySpecialty || doc.subname || cat.categoryName }}
                </p>

                <!-- Rating Stars & Count -->
                <div
                  class="rating-box mb-3 d-flex justify-content-center
                    align-items-center"
                >
                  <div class="text-warning mr-2">
                    <font-awesome-icon
                      v-for="s in 5"
                      :key="s"
                      :icon="getStarIcon(doc.ratingAvg, s)"
                    />
                  </div>
                  <strong class="text-dark small font-weight-bold">
                    {{ (doc.ratingAvg != null ? doc.ratingAvg : 0).toFixed(1) }}
                  </strong>
                  <span class="text-muted small ml-1">
                    ({{ doc.ratingCount || 0 }})
                  </span>
                </div>

                <!-- Action buttons -->
                <div class="mt-auto d-flex justify-content-center gap-2">
                  <a
                    v-if="doc.profileUrl"
                    :href="`/_/${doc.profileUrl}`"
                    class="btn btn-outline-primary-base btn-sm rounded-pill
                      px-3 font-weight-bold"
                  >
                    Ver Perfil
                  </a>
                  <mdb-btn
                    type="button"
                    color="success-base"
                    size="sm"
                    rounded
                    class="px-3 font-weight-bold"
                    @click="openAgendarModal(doc)"
                  >
                    Agendar
                  </mdb-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 3. Instituciones Médicas Destacadas -->
    <section
      v-if="featuredInstitutions && featuredInstitutions.length"
      class="py-5 bg-white border-bottom"
    >
      <div class="container">
        <div class="text-center mb-5">
          <span
            class="badge badge-primary-base px-3 py-2 text-uppercase
              font-weight-bold mb-2"
          >
            Centros de Salud
          </span>
          <h2 class="h2 font-weight-bold text-dark-blue mb-1">
            Instituciones Médicas Destacadas
          </h2>
          <p class="text-muted small">
            Clínicas, centros y laboratorios con tecnología de vanguardia
          </p>
        </div>

        <div class="row">
          <div
            v-for="inst in featuredInstitutions"
            :key="inst.id"
            class="col-12 col-md-6 col-lg-4 mb-4"
          >
            <div
              class="card h-100 shadow-sm border-0 rounded-xl
                institution-card p-4 text-center"
            >
              <div class="featured-avatar-wrapper">
                <div class="featured-glow-halo" />
                <img
                  :src="getInstitutionImage(inst)"
                  :alt="inst.name"
                  class="featured-avatar"
                >
              </div>

              <h4 class="h5 font-weight-bold text-dark-blue mb-1">
                {{ inst.name }}
              </h4>

              <p
                v-if="inst.city || inst.address"
                class="text-muted small mb-2"
              >
                <font-awesome-icon
                  icon="map-marker-alt"
                  class="mr-1 text-primary-base"
                />
                {{ inst.city ? inst.city : '' }}
                {{ inst.city && inst.address ? '—' : '' }}
                {{ inst.address ? inst.address : '' }}
              </p>

              <!-- Rating -->
              <div
                class="rating-box mb-3 d-flex justify-content-center
                  align-items-center"
              >
                <div class="text-warning mr-2">
                  <font-awesome-icon
                    v-for="s in 5"
                    :key="s"
                    :icon="getStarIcon(inst.ratingAvg, s)"
                  />
                </div>
                <strong class="text-dark small font-weight-bold">
                  {{
                    (inst.ratingAvg != null ? inst.ratingAvg : 0).toFixed(1)
                  }}
                </strong>
                <span class="text-muted small ml-1">
                  ({{ inst.ratingCount || 0 }})
                </span>
              </div>

              <div class="mt-auto">
                <a
                  v-if="inst.profileUrl"
                  :href="`/_/${inst.profileUrl}`"
                  class="btn btn-outline-primary-base btn-sm rounded-pill
                    px-4 font-weight-bold"
                >
                  Ver Centro
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 4. Buscador y Directorio General -->
    <section
      id="buscador-general"
      class="py-5 bg-light"
    >
      <div class="container">
        <!-- Banner destacado Teleconsulta -->
        <div
          class="card border-0 shadow-sm p-4 rounded-xl
            bg-primary-dark-base text-white mb-4"
        >
          <div
            class="d-flex flex-wrap align-items-center
              justify-content-between"
          >
            <div class="col-12 col-md-8 px-0 mb-3 mb-md-0">
              <span
                class="badge badge-light text-primary-base font-weight-bold
                  text-uppercase mb-2 px-3 py-1"
              >
                Atención Inmediata
              </span>
              <h3 class="h4 font-weight-bold mb-1">
                ¿Prefieres atenderte desde casa?
              </h3>
              <p class="mb-0 small text-white-50">
                Accede a nuestro nuevo espacio de Consultas Virtuales y
                conoce a los especialistas disponibles en línea para
                teleconsulta.
              </p>
            </div>
            <div class="col-12 col-md-auto px-0">
              <router-link
                to="/consulta-virtual"
                class="btn btn-white text-primary-base font-weight-bold
                  rounded-pill px-4 py-2 shadow-sm text-nowrap"
              >
                <font-awesome-icon
                  icon="video"
                  class="mr-2"
                />
                Ver Consultas Virtuales
              </router-link>
            </div>
          </div>
        </div>

        <div class="card shadow-lg p-4 rounded-xl bg-white text-dark mb-4">
          <div class="text-center mb-4">
            <span
              class="badge badge-primary-base px-3 py-2 text-uppercase
                font-weight-bold mb-2"
            >
              Búsqueda Completa
            </span>
            <h2 class="h3 font-weight-bold text-dark-blue mb-1">
              Buscador de Especialistas
            </h2>
            <p class="text-muted small">
              Busca por nombre, especialidad o ubicación para agendar tu cita.
            </p>
          </div>

          <div class="row align-items-center">
            <div class="col-12 col-md">
              <mdb-input
                v-model="search.name"
                label="Nombre del Médico"
                class="my-2"
                outline
              />
            </div>
            <div class="col-12 col-md">
              <div class="md-form md-outline outline-select my-2">
                <select
                  id="searchSpecialityDir"
                  v-model="search.speciality"
                  class="custom-select"
                  @change="resetPagination"
                >
                  <option
                    class="d-none"
                    disabled
                    value=""
                  >
                    Seleccione
                  </option>
                  <option
                    v-for="speciality in tags"
                    :key="speciality.name"
                    :value="speciality.name"
                  >
                    {{ speciality.name }}
                  </option>
                </select>
                <label
                  for="searchSpecialityDir"
                  :class="search.speciality && search.speciality.length
                    ? 'label-active' : 'label-inactive'"
                >
                  Especialidad
                </label>
              </div>
            </div>
            <div
              v-if="search.speciality"
              class="col-12 col-md-auto"
            >
              <mdb-btn
                type="button"
                class="p-2 z-depth-0 w-100 mx-0"
                icon="broom"
                flat
                dark-waves
                @click="search.speciality = ''"
              >
                Limpiar
              </mdb-btn>
            </div>
          </div>

          <!-- Location Toggle Button -->
          <div class="text-center my-2">
            <mdb-btn
              type="button"
              flat
              dark-waves
              class="px-3 py-2 text-capitalize"
              @click="toggleLocationFilter"
            >
              <font-awesome-icon
                :icon="filterSearchBy ? 'check-square' : 'square'"
                class="mr-2"
                :class="filterSearchBy ? 'text-primary-base' : 'text-muted'"
              />
              <span
                :class="filterSearchBy
                  ? 'font-weight-bold text-dark-blue'
                  : 'text-muted'"
              >
                Filtrar por ubicación (País, Estado, Ciudad)
              </span>
            </mdb-btn>
          </div>

          <!-- Virtual Consultation Filter -->
          <div class="text-center my-2">
            <label
              class="cursor-pointer small font-weight-bold text-dark-blue
                d-inline-flex align-items-center mb-0 py-1 px-2"
            >
              <input
                v-model="search.isVirtual"
                type="checkbox"
                class="mr-2"
                @change="resetPagination"
              >
              <font-awesome-icon
                icon="video"
                class="mr-1 text-primary-base"
              />
              Solo médicos con opción de Consulta Virtual (Teleconsulta)
            </label>
          </div>

          <!-- Location Filters -->
          <div
            v-if="filterSearchBy"
            class="row justify-content-center mx-0 mt-2"
          >
            <div class="col-12 col-sm-6 col-xl-3">
              <div class="md-form md-outline outline-select my-2">
                <select
                  id="searchCountryDir"
                  v-model="search.country"
                  class="custom-select"
                  required
                  @change="search.state = ''; resetPagination();"
                >
                  <option
                    class="d-none"
                    disabled
                    value=""
                  >
                    Seleccione
                  </option>
                  <option
                    v-for="country in countries"
                    :key="country.id"
                    :value="country.id"
                  >
                    {{ country.name }}
                  </option>
                </select>
                <label
                  for="searchCountryDir"
                  :class="search.country && search.country.length
                    ? 'label-active' : 'label-inactive'"
                >
                  País
                </label>
              </div>
            </div>

            <div class="col-12 col-sm-6 col-xl-3">
              <div class="md-form md-outline outline-select my-2">
                <select
                  id="searchStateDir"
                  v-model="search.state"
                  :class="['custom-select', {
                    'no-background': isStatesLoading
                  }]"
                  required
                  @change="search.city = ''; resetPagination();"
                >
                  <option
                    class="d-none"
                    disabled
                    value=""
                  >
                    Seleccione
                  </option>
                  <option
                    v-for="state in states"
                    :key="state.id"
                    :value="state.id"
                  >
                    {{ state.name }}
                  </option>
                </select>
                <label
                  for="searchStateDir"
                  :class="search.state && search.state.length
                    ? 'label-active' : 'label-inactive'"
                >
                  Estado
                </label>
              </div>
            </div>

            <div class="col-12 col-sm-6 col-xl-3">
              <div class="md-form md-outline outline-select my-2">
                <select
                  id="searchCityDir"
                  v-model="search.city"
                  :class="['custom-select', {
                    'no-background': isCitiesLoading
                  }]"
                  required
                  @change="resetPagination"
                >
                  <option
                    class="d-none"
                    disabled
                    value=""
                  >
                    Seleccione
                  </option>
                  <option
                    v-for="city in cities"
                    :key="city.id"
                    :value="city.id"
                  >
                    {{ city.name }}
                  </option>
                </select>
                <label
                  for="searchCityDir"
                  :class="search.city && search.city.length
                    ? 'label-active' : 'label-inactive'"
                >
                  Ciudad
                </label>
              </div>
            </div>
          </div>

          <!-- Doctor Cards Results -->
          <div
            v-if="!skipDoctors"
            id="resultados-busqueda"
            ref="resultadosBusqueda"
            class="mt-4"
          >
            <p
              v-if="isDoctorsLoading"
              class="text-center text-uppercase"
            >
              <font-awesome-icon
                icon="circle-notch"
                size="lg"
                class="fa-spin mr-2"
              />
              Buscando especialistas...
            </p>
            <div
              v-if="doctors && doctors.length"
              class="d-flex flex-wrap gap-2"
            >
              <TarjetaResultadoDoctor
                v-for="doc in doctors"
                :key="doc.id"
                :doc="doc"
                class="col-12 col-md-6 col-lg-4 px-2 mb-3"
                @modal-ver-detalles="doctor = doc; showAgendarCita = true"
              >
                <template #botones>
                  <div
                    :class="[
                      'd-flex mx-n1',
                      doc.profileUrl
                        ? 'justify-content-between'
                        : 'justify-content-end',
                    ]"
                  >
                    <a
                      v-if="doc.profileUrl"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      :href="`/_/${doc.profileUrl}`"
                      @click.stop=""
                    >
                      <mdb-btn
                        type="button"
                        class="p-2 z-depth-0"
                        size="sm"
                        icon="user-circle"
                        icon-class="fa-lg"
                        outline="primary-base"
                        rounded
                      >
                        Perfil
                      </mdb-btn>
                    </a>
                    <mdb-btn
                      type="button"
                      class="py-2 px-3 font-weight-bold"
                      color="success-base"
                      size="sm"
                      icon="calendar-alt"
                      rounded
                      @click.stop="doctor = doc; showAgendarCita = true"
                    >
                      Agendar
                    </mdb-btn>
                  </div>
                </template>
              </TarjetaResultadoDoctor>
            </div>

            <!-- Botón Ver Más / Paginación -->
            <div
              v-if="doctors && doctors.length === pageSize * page"
              class="col-12 d-flex justify-content-center m-0 mt-3"
            >
              <mdb-btn
                type="button"
                color="primary-base"
                @click="showMoreSearchDoctors"
              >
                Ver Más
                <font-awesome-icon
                  icon="eye"
                  class="ml-1"
                />
              </mdb-btn>
            </div>

            <p
              v-else-if="!isDoctorsLoading &&
                (!doctors || !doctors.length)"
              class="text-center text-muted my-3"
            >
              No se encontraron médicos con los filtros seleccionados.
            </p>
          </div>
        </div>

        <AvisoVeracidadEspecialidad class="mt-3 mb-2" />
      </div>
    </section>

    <!-- 5. Portal para Pacientes -->
    <section
      id="acceso-pacientes"
      class="py-5 bg-white border-top"
    >
      <div class="container">
        <div class="text-center mb-5">
          <span
            class="badge badge-success-base text-uppercase px-3 py-2
              font-weight-bold mb-2 rounded-pill"
          >
            Portal de Pacientes
          </span>
          <h2 class="h2 font-weight-bold text-dark-blue mb-2">
            Tu Salud en un Solo Lugar
          </h2>
          <p class="text-muted lead max-w-700 mx-auto">
            Disfruta de una experiencia médica personalizada y toma el control
            de tus citas y tratamientos con tu cuenta de paciente AMII.
          </p>
        </div>

        <div class="row justify-content-center mb-5">
          <!-- Ventaja 1 -->
          <div class="col-12 col-md-6 col-lg-3 mb-4">
            <div
              class="card h-100 shadow-sm border-0 rounded-xl p-4 text-center
                patient-feature-card"
            >
              <div class="patient-icon-wrapper mb-3 mx-auto">
                <font-awesome-icon
                  icon="calendar-check"
                  class="patient-feature-icon"
                />
              </div>
              <h3 class="h5 font-weight-bold text-dark-blue mb-2">
                Citas Recurrentes
              </h3>
              <p class="text-muted small mb-0">
                Agenda citas recurrentes más fácil y rápido con tus
                especialistas e instituciones preferidas.
              </p>
            </div>
          </div>

          <!-- Ventaja 2 -->
          <div class="col-12 col-md-6 col-lg-3 mb-4">
            <div
              class="card h-100 shadow-sm border-0 rounded-xl p-4 text-center
                patient-feature-card"
            >
              <div class="patient-icon-wrapper mb-3 mx-auto">
                <font-awesome-icon
                  icon="clock"
                  class="patient-feature-icon"
                />
              </div>
              <h3 class="h5 font-weight-bold text-dark-blue mb-2">
                Control de Tratamientos
              </h3>
              <p class="text-muted small mb-0">
                Lleva el control de tratamientos médicos con recordatorios
                personalizados para su toma o aplicación.
              </p>
            </div>
          </div>

          <!-- Ventaja 3 -->
          <div class="col-12 col-md-6 col-lg-3 mb-4">
            <div
              class="card h-100 shadow-sm border-0 rounded-xl p-4 text-center
                patient-feature-card"
            >
              <div class="patient-icon-wrapper mb-3 mx-auto">
                <font-awesome-icon
                  icon="star"
                  class="patient-feature-icon text-warning"
                />
              </div>
              <h3 class="h5 font-weight-bold text-dark-blue mb-2">
                Opiniones y Calificación
              </h3>
              <p class="text-muted small mb-0">
                Opina y califica la consulta médica a la que fuiste para
                ayudar y mejorar la comunidad.
              </p>
            </div>
          </div>

          <!-- Ventaja 4 -->
          <div class="col-12 col-md-6 col-lg-3 mb-4">
            <div
              class="card h-100 shadow-sm border-0 rounded-xl p-4 text-center
                patient-feature-card"
            >
              <div class="patient-icon-wrapper mb-3 mx-auto">
                <font-awesome-icon
                  icon="calendar-alt"
                  class="patient-feature-icon"
                />
              </div>
              <h3 class="h5 font-weight-bold text-dark-blue mb-2">
                Próximas Citas
              </h3>
              <p class="text-muted small mb-0">
                Visualiza y gestiona todas tus próximas citas agendadas
                desde un único panel siempre a mano.
              </p>
            </div>
          </div>
        </div>

        <!-- CTA Box -->
        <div
          class="patient-cta-box text-center p-4 p-md-5 rounded-xl
            text-white"
        >
          <h3 class="h3 font-weight-bold mb-3">
            ¿Listo para gestionar tus consultas y tratamientos?
          </h3>
          <p class="lead max-w-700 mx-auto opacity-90 mb-4">
            Accede al portal de pacientes de AMII desde cualquier dispositivo
            y lleva tu salud al siguiente nivel.
          </p>
          <a
            href="https://paciente.amii.app"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-success-base btn-md px-5 py-3 font-weight-bold
              rounded-pill text-white shadow text-decoration-none d-inline-flex
              align-items-center"
          >
            <font-awesome-icon
              icon="sign-in-alt"
              class="mr-2"
            />
            Acceder como paciente
          </a>
        </div>
      </div>
    </section>

    <!-- Modals -->
    <modalAgendarCita
      :show-modal="showAgendarCita"
      :doctor="doctor"
      @close="showAgendarCita = false; doctor = {}"
    />

    <AlertaMsj
      :alerta-msj="alertaMsj"
      @cerrar="alertaMsj.content = ''"
    />
  </div>
</template>

<script>
import AlertaMsj from '@/components/AlertaMsj.vue';
import {mdbBtn, mdbInput} from 'mdbvue';
import TarjetaResultadoDoctor from '@/components/TarjetaResultadoDoctor';
import modalAgendarCita from '@/components/modalAgendarCita';
import gql from 'graphql-tag';

import featuredDoctorsGql from '@/graphql/featuredDoctors.gql';
import featuredInstitutionsGql from '@/graphql/featuredInstitutions.gql';
import countriesGql from '@/graphql/countries.gql';
import statesGql from '@/graphql/states.gql';
import citiesGql from '@/graphql/cities.gql';
import especialidadesGql from '@/graphql/especialidades.gql';
import doctorsSearch from '@/graphql/doctorsSearch.gql';
import AvisoVeracidadEspecialidad
from '@/components/AvisoVeracidadEspecialidad.vue';

const imgPath = process.env.NODE_ENV === 'production'
  ? process.env.VUE_APP_MULTIMEDIA_HTTPS
  : process.env.VUE_APP_MULTIMEDIA_HTTP;

export default {
  name: 'Directorio',
  components: {
    AlertaMsj,
    AvisoVeracidadEspecialidad,
    mdbBtn,
    mdbInput,
    TarjetaResultadoDoctor,
    modalAgendarCita,
  },
  data() {
    return {
      pageSize: 10,
      page: 1,
      alertaMsj: {content: ''},
      showAgendarCita: false,
      search: {
        country: '',
        state: '',
        city: '',
        name: '',
        speciality: '',
        isVirtual: false,
      },
      doctors: [],
      doctor: {},
      filterSearchBy: true,
      imgPath: imgPath + 'dir/',
      featuredCategories: [],
      featuredInstitutions: [],
    };
  },
  computed: {
    isStatesLoading() {
      return !!(this.$apolloData?.queries?.states?.loading);
    },
    isCitiesLoading() {
      return !!(this.$apolloData?.queries?.cities?.loading);
    },
    isDoctorsLoading() {
      return !!(this.$apolloData?.queries?.doctors?.loading);
    },
    skipDoctors() {
      if (
        this.search.name.length > 2
        || this.search.speciality
        || this.search.isVirtual
        || this.search.country
        || this.search.state
        || this.search.city
      ) {
        return false;
      }
      return true;
    },
  },
  watch: {
    doctors(newVal) {
      if (newVal && newVal.length > 0 && this.page === 1) {
        this.scrollToResults();
      }
    },
  },
  mounted() {
    this.disableChatwoot();
  },
  beforeDestroy() {
    this.restoreChatwoot();
  },
  methods: {
    disableChatwoot() {
      if (typeof window === 'undefined') return;
      const hideFn = () => {
        if (window.$chatwoot) {
          if (typeof window.$chatwoot.toggle === 'function') {
            window.$chatwoot.toggle('hide');
          } else if (typeof window.$chatwoot.hide === 'function') {
            window.$chatwoot.hide();
          }
        }
        const chatwootWidget = document.querySelector('.woot-widget-holder')
          || document.querySelector('.woot--bubble-holder')
          || document.getElementById('chatwoot-widget-container');
        if (chatwootWidget) {
          chatwootWidget.style.display = 'none';
        }
      };
      hideFn();
      setTimeout(hideFn, 300);
      setTimeout(hideFn, 1000);
    },
    restoreChatwoot() {
      if (typeof window === 'undefined') return;
      if (window.$chatwoot) {
        if (typeof window.$chatwoot.toggle === 'function') {
          window.$chatwoot.toggle('show');
        } else if (typeof window.$chatwoot.show === 'function') {
          window.$chatwoot.show();
        }
      }
      const chatwootWidget = document.querySelector('.woot-widget-holder')
        || document.querySelector('.woot--bubble-holder')
        || document.getElementById('chatwoot-widget-container');
      if (chatwootWidget) {
        chatwootWidget.style.display = '';
      }
    },
    toggleLocationFilter() {
      this.filterSearchBy = !this.filterSearchBy;
      if (!this.filterSearchBy) {
        this.search.country = '';
        this.search.state = '';
        this.search.city = '';
        this.resetPagination();
      }
    },
    scrollToSearch() {
      const el = document.getElementById('buscador-general');
      if (el) el.scrollIntoView({behavior: 'smooth'});
    },
    scrollToFeatured() {
      const el = document.getElementById('medicos-destacados');
      if (el) el.scrollIntoView({behavior: 'smooth'});
    },
    scrollToResults() {
      if (this.page !== 1) return;
      this.$nextTick(() => {
        const el = document.getElementById('resultados-busqueda');
        if (el) {
          const navOffset = 80;
          const elementPosition = el.getBoundingClientRect().top;
          const offsetPosition =
            elementPosition + window.pageYOffset - navOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      });
    },
    openAgendarModal(doc) {
      this.doctor = {
        ...doc,
        profileInfo: {
          image: doc.imageUrl ? {url: doc.imageUrl} : null,
          avatar: doc.avatar,
          subname: doc.subname,
        },
      };
      this.showAgendarCita = true;
    },
    getDoctorAvatar(doc) {
      if (doc.imageUrl) {
        if (doc.imageUrl.startsWith('http')) return doc.imageUrl;
        return `${this.imgPath}${doc.imageUrl}`;
      }
      if (doc.avatar) {
        return `https://www.gravatar.com/avatar/${doc.avatar}?s=150`;
      }
      return 'https://www.gravatar.com/avatar/?s=150&d=mp';
    },
    getInstitutionImage(inst) {
      if (inst.imageUrl) {
        if (inst.imageUrl.startsWith('http')) return inst.imageUrl;
        return `${this.imgPath}${inst.imageUrl}`;
      }
      return 'https://www.gravatar.com/avatar/?s=150&d=mp';
    },
    getDoctorFullName(doc) {
      const parts = [
        doc.prename,
        doc.fname,
        doc.mname,
        doc.lname,
        doc.llname,
        doc.posname,
      ].filter(Boolean);
      return parts.join(' ');
    },
    getStarIcon(avg, star) {
      const rate = avg != null ? avg : 0;
      if (star <= Math.floor(rate)) {
        return ['fas', 'star'];
      } else if (star - 0.5 <= rate) {
        return ['fas', 'star-half-alt'];
      }
      return ['far', 'star'];
    },
    resetPagination() {
      this.page = 1;
    },
    showMoreSearchDoctors() {
      this.page++;
      this.$apollo.queries.doctors.fetchMore({
        variables: {
          name: this.search.name,
          countryId: this.search.country,
          stateId: this.search.state,
          cityId: this.search.city,
          degree: this.search.speciality,
          isVirtual: this.search.isVirtual || null,
          first: this.pageSize,
          offset: (this.page - 1) * this.pageSize,
        },
        updateQuery: (previousResult, {fetchMoreResult}) => {
          if (!fetchMoreResult) return previousResult;
          return {
            __typename: previousResult.doctorsSearch.__typename,
            doctorsSearch: [
              ...previousResult.doctorsSearch,
              ...fetchMoreResult.doctorsSearch,
            ],
          };
        },
      });
    },
  },
  apollo: {
    featuredCategories() {
      return {
        query: gql`${featuredDoctorsGql}`,
        variables: {
          limitPerCategory: 3,
        },
        update: (data) => data.featuredDoctors || [],
      };
    },
    featuredInstitutions() {
      return {
        query: gql`${featuredInstitutionsGql}`,
        variables: {
          limit: 6,
        },
        update: (data) => data.featuredInstitutions || [],
      };
    },
    countries() {
      return {
        query: gql`${countriesGql}`,
      };
    },
    states() {
      return {
        query: gql`${statesGql}`,
        variables() {
          return {id: this.search.country};
        },
        skip() {
          return !this.search.country;
        },
      };
    },
    cities() {
      return {
        query: gql`${citiesGql}`,
        variables() {
          return {id: this.search.state};
        },
        skip() {
          return !this.search.state;
        },
      };
    },
    tags() {
      return {
        query: gql`${especialidadesGql}`,
      };
    },
    doctors() {
      return {
        query() {
          return gql`${doctorsSearch}`;
        },
        variables() {
          return {
            name: this.search.name,
            countryId: this.search.country,
            stateId: this.search.state,
            cityId: this.search.city,
            degree: this.search.speciality,
            isVirtual: this.search.isVirtual || null,
            first: this.pageSize,
            offset: 0,
          };
        },
        skip() {
          return this.skipDoctors;
        },
        debounce: 1000,
        result({loading}) {
          if (!loading && !this.skipDoctors && this.page === 1) {
            this.scrollToResults();
          }
        },
        update: (data) => data.doctorsSearch,
      };
    },
  },
};
</script>

<style scoped>
.directorio-page {
  padding-top: 64px;
}
.text-dark-blue {
  color: #002196;
}
.max-w-700 {
  max-width: 700px;
}
.rounded-xl {
  border-radius: 16px;
}
.gap-2 {
  gap: 0.75rem;
}
.featured-doctor-card,
.institution-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.featured-doctor-card:hover,
.institution-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 33, 150, 0.12) !important;
}
.featured-avatar-wrapper {
  position: relative;
  width: 96px;
  height: 96px;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.featured-glow-halo {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(0, 200, 81, 0.4) 0%,
    rgba(0, 33, 150, 0.25) 55%,
    transparent 75%
  );
  filter: blur(8px);
  animation: ambient-pulse 3s ease-in-out infinite;
  z-index: 1;
}
.featured-avatar {
  position: relative;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  object-fit: cover;
  background-color: #fff;
  border: 2.5px solid #ffffff;
  z-index: 2;
  box-shadow: 0 6px 16px rgba(0, 33, 150, 0.14);
}
@keyframes ambient-pulse {
  0%, 100% {
    transform: scale(0.95);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.15);
    opacity: 1;
  }
}
.patient-feature-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background-color: #ffffff;
}
.patient-feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 33, 150, 0.1) !important;
}
.patient-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: rgba(0, 33, 150, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
}
.patient-feature-icon {
  font-size: 1.75rem;
  color: #002196;
}
.patient-cta-box {
  background: linear-gradient(135deg, #002196 0%, #001254 100%);
  box-shadow: 0 16px 32px rgba(0, 33, 150, 0.2);
}
</style>
